import { useState, useEffect } from 'react'
import { useAdmin } from '../context/AdminContext'
import { createAdminApi, SessionDetail, LLMMessage } from '../services/adminApi'
import styles from './SessionDetailPage.module.css'

function buildJoinUrl(detail: SessionDetail): string {
  const params = new URLSearchParams({ session_id: detail.id })
  if (detail.features?.length) params.set('features', detail.features.join(','))
  if (detail.character) params.set('character', detail.character)
  if (detail.storyPath) params.set('story', getStoryId(detail.storyPath))
  return `?${params}#chat`
}

function getStoryId(storyPath: string): string {
  const parts = storyPath.split('/')
  const filename = parts[parts.length - 1]
  return filename.replace('.yaml', '')
}

interface Props {
  sessionId: string
}

export default function SessionDetailPage({ sessionId }: Props) {
  const { apiKey } = useAdmin()

  const [detail, setDetail] = useState<SessionDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    setLoading(true)
    setError('')
    const api = createAdminApi(() => apiKey)
    api.getSession(sessionId)
      .then(d => { setDetail(d); setLoading(false) })
      .catch(e => { setError(e instanceof Error ? e.message : String(e)); setLoading(false) })
  }, [sessionId, apiKey])

  function msgClass(msg: LLMMessage) {
    return msg.role === 'user' ? styles.userMsg : styles.assistantMsg
  }

  if (loading) {
    return (
      <div className={styles.page}>
        <header className={styles.header}>
          <a href="#sessions" className={styles.back}>← Sessions</a>
        </header>
        <p className={styles.loading}>Loading…</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.page}>
        <header className={styles.header}>
          <a href="#sessions" className={styles.back}>← Sessions</a>
        </header>
        <p className={styles.errorMsg}>{error}</p>
      </div>
    )
  }

  if (!detail) return null

  const completedObjectives = detail.completedObjectives
    ? Object.entries(detail.completedObjectives).filter(([, done]) => done)
    : []
  const playerStateEntries = detail.playerState ? Object.entries(detail.playerState) : []

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <a href="#sessions" className={styles.back}>← Sessions</a>
        <h1>Session <code>{detail.id}</code></h1>
        <a className={styles.joinBtn} href={buildJoinUrl(detail)}>Join Session</a>
      </header>

      <div className={styles.content}>
        <section className={styles.meta}>
          <div className={styles.metaItem}>
            <span>Character</span>
            <strong>{detail.character || '—'}</strong>
          </div>
          <div className={styles.metaItem}>
            <span>Story</span>
            <strong>{detail.storyPath || '—'}</strong>
          </div>
          <div className={styles.metaItem}>
            <span>Connections</span>
            <strong>{detail.connections}</strong>
          </div>
          <div className={styles.metaItem}>
            <span>Phase</span>
            <strong>{detail.storyPhase || '—'}</strong>
          </div>
          <div className={styles.metaItem}>
            <span>Turn</span>
            <strong>{detail.turnCount ?? '—'}</strong>
          </div>
          {detail.features?.length > 0 && (
            <div className={styles.metaItem}>
              <span>Features</span>
              <strong>{detail.features.join(', ')}</strong>
            </div>
          )}
        </section>

        {detail.conversationSummary && (
          <section className={styles.section}>
            <h2>Conversation Summary</h2>
            <p className={styles.summaryText}>{detail.conversationSummary}</p>
          </section>
        )}

        {(completedObjectives.length > 0 || playerStateEntries.length > 0 || detail.lastStorytellerReasoning) && (
          <section className={styles.section}>
            <h2>Storyteller State</h2>
            {completedObjectives.length > 0 && (
              <div className={styles.storyBlock}>
                <h3>Completed Objectives</h3>
                <ul className={styles.objectiveList}>
                  {completedObjectives.map(([id]) => (
                    <li key={id}>{id}</li>
                  ))}
                </ul>
              </div>
            )}
            {playerStateEntries.length > 0 && (
              <div className={styles.storyBlock}>
                <h3>Player State</h3>
                <dl className={styles.stateList}>
                  {playerStateEntries.map(([k, v]) => (
                    <div key={k} className={styles.stateEntry}>
                      <dt>{k}</dt>
                      <dd>{v}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
            {detail.lastStorytellerReasoning && (
              <div className={styles.storyBlock}>
                <h3>Last Reasoning</h3>
                <p className={styles.reasoningText}>{detail.lastStorytellerReasoning}</p>
              </div>
            )}
          </section>
        )}

        <section className={styles.section}>
          <h2>Message History ({detail.history.length})</h2>
          {detail.history.length === 0 ? (
            <p className={styles.emptyHistory}>No messages yet.</p>
          ) : (
            <div className={styles.messages}>
              {detail.history.map((msg, i) => (
                <div key={i} className={`${styles.message} ${msgClass(msg)}`}>
                  <div className={styles.msgRole}>{msg.role}</div>
                  <div className={styles.msgContent}>{msg.content}</div>
                  {msg.type && <div className={styles.msgType}>{msg.type}</div>}
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  )
}
